import {useCallback, useMemo} from "react";
import { GridColDef } from "@mui/x-data-grid-pro";
import { useTranslation } from "react-i18next";
import {PersonDto, PersonFilterDto} from "../../client/person/PersonApiClient";
import { PersonTranslKey } from "./PersonTranslKey";
import { DateTimeFormatter } from "../../common/DateTimeFormatter";
import PageableGrid, {PageLoadSize} from "../../common/pagination/PageableGrid";
import {FilterSearchType} from "./PersonSearchPanel";
import {RobSubscriptionStatusFormatter} from "./RobSubscriptionStatusFormatter";
import {PersonDataOriginFormatter} from "./PersonDataOriginFormatter";

function buildPersonGridColumns(t: (key: string) => string): GridColDef[] {
    return [
        {
            field: "firstName",
            headerName: t(PersonTranslKey.PERSON_FIRSTNAME),
            width: 150,
            sortable: false
        },
        {
            field: "lastName",
            headerName: t(PersonTranslKey.PERSON_LASTNAME),
            width: 150,
            sortable: false
        },
        {
            field: "email",
            headerName: t(PersonTranslKey.PERSON_EMAIL),
            width: 250,
            sortable: false
        },
        {
            field: "phone",
            headerName: t(PersonTranslKey.PERSON_PHONE),
            width: 120,
            sortable: false
        },
        {
            field: "dataOrigin",
            headerName: t(PersonTranslKey.PERSON_DATA_ORIGIN),
            width: 180,
            sortable: false,
            valueFormatter: params => PersonDataOriginFormatter.formatDataOrigin(params.value, t)
        },
        {
            field: "subscriptionStatus",
            headerName: t(PersonTranslKey.PERSON_SUBSCRIPTION_STATUS),
            width: 250,
            sortable: false,
            valueFormatter: params => RobSubscriptionStatusFormatter.formatSubscriptionStatus(params.value, t)
        },
        {
            field: "robUpdatedAt",
            headerName: t(PersonTranslKey.PERSON_ISZR_UPDATED_AT),
            width: 200,
            sortable: false,
            valueGetter: params => params.value ? new Date(params.value) : undefined,
            valueFormatter: params => DateTimeFormatter.formatDateTime(params.value)
        },
    ];
}

export interface PersonGridProps {
    rowsData: PersonDto[];
    totalPersonCount: number;
    selectedPersonId?: number;

    nextPageToken?: string | null;
    personFilter: PersonFilterDto;
    fetchPersons: (filter: PersonFilterDto, searchType: FilterSearchType) => void;

    onSelectPerson?: (person: PersonDto) => void;
    onPersonDoubleClick?: (personId: number) => void;
}

export default function PersonGrid(props: Readonly<PersonGridProps>) {
    const {rowsData, totalPersonCount, selectedPersonId, nextPageToken, personFilter, fetchPersons, onSelectPerson, onPersonDoubleClick} = props;

    const {t} = useTranslation();
    const columns = useMemo(() => buildPersonGridColumns(t), [t]);

    const handleFetchMore = useCallback(() => {
        if (!nextPageToken) {
            return;
        }

        const nextPageFilter: PersonFilterDto = {
            ...personFilter,
            paging: {
                maxPageSize: PageLoadSize.NEXT_PAGE_SIZE,
                pageToken: nextPageToken,
            },
        };

        fetchPersons(nextPageFilter, FilterSearchType.NEXT_PAGE_SEARCH);
    }, [nextPageToken, personFilter, fetchPersons]);

    return (
        <PageableGrid
            rowsData={rowsData}
            totalRowsCount={totalPersonCount}
            columns={columns}
            onFetchMore={handleFetchMore}
            selectedRowId={selectedPersonId}
            onRowSelect={onSelectPerson}
            onRowDoubleClick={onPersonDoubleClick}
        />
    );
}
